import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { Star, MapPin, Users, Check, ChevronLeft, Map as MapIcon, Plus, Minus, Globe, ChevronRight, Maximize, Play, Pause, X, ExternalLink, Crown, Info, BookmarkCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { Map, Marker } from "pigeon-maps";
import { formatNaira, getStartingPrice, hasGuestRating, CATEGORY_DETAILS, FACILITY_PHOTO_LABELS, type RoomType, type Venue } from "@/data/venues";
import { VenueService } from "@/services/venue.service";
import { Button } from "@/components/ui/button";
import { VenueCard } from "@/components/venues/VenueCard";
import { Reviews } from "@/components/venues/Reviews";

export const Route = createFileRoute("/venues/$venueId")({
  loader: async ({ params }) => {
    const venue = await VenueService.getVenueById(params.venueId);
    if (!venue) throw notFound();
    return { venue };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { venue } = loaderData;
    return {
      meta: [
        { title: `${venue.name} — OyoBooking` },
        { name: "description", content: venue.description },
        { property: "og:title", content: `${venue.name} — OyoBooking` },
        { property: "og:description", content: venue.description },
        { property: "og:image", content: venue.image },
        {
          "script:ld+json": {
            "@context": "https://schema.org",
            "@type": venue.category === "hotel" ? "LodgingBusiness" : "EventVenue",
            "name": venue.name,
            "description": venue.description,
            "image": venue.image,
            "telephone": "+234-800-OYO-BOOK",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": venue.location,
              "addressRegion": "Oyo State",
              "addressCountry": "NG",
            },
            ...(venue.reviews > 0 ? {
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": venue.rating,
                "reviewCount": venue.reviews,
              },
            } : {}),
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "NGN",
              "lowPrice": venue.price,
            },
          },
        },
      ],
      links: [{ rel: "canonical", href: `https://oyobooking.ng/venues/${venue.id}` }],
    };
  },
  component: VenueDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-md py-32 text-center">
      <h1 className="font-display text-3xl font-bold">Venue not found</h1>
      <Link to="/hotels"><Button className="mt-6">Browse hotels</Button></Link>
    </div>
  ),
});

const CITY_COORDINATES: Record<string, [number, number]> = {
  Ibadan: [7.3775, 3.9470],
  Ogbomoso: [8.1227, 4.2436],
  Saki: [8.6676, 3.3958],
  Iseyin: [7.9717, 3.5901],
  Kisi: [9.0825, 3.8496],
  Igboho: [8.8354, 3.7533],
};

const voyagerProvider = (x: number, y: number, z: number, dpr?: number) => {
  return `https://basemaps.cartocdn.com/rastertiles/voyager/${z}/${x}/${y}${dpr && dpr >= 2 ? "@2x" : ""}.png`;
};

const esriSatelliteProvider = (x: number, y: number, z: number) => {
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
};

function VenueDetail() {
  const { venue } = Route.useLoaderData();
  const router = useRouter();
  const [active, setActive] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [mapMode, setMapMode] = useState<"street" | "satellite">("satellite");
  const [zoom, setZoom] = useState(13);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const selectedRoom = venue.roomTypes?.find((r: RoomType) => r.id === selectedRoomId) || null;
  const displayPrice = selectedRoom ? selectedRoom.price : getStartingPrice(venue);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Slideshow Autoplay Effect
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % venue.gallery.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPlaying, venue.gallery.length]);

  // Keyboard navigation for Lightbox & Carousel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setActive(prev => (prev - 1 + venue.gallery.length) % venue.gallery.length);
      } else if (e.key === "ArrowRight") {
        setActive(prev => (prev + 1) % venue.gallery.length);
      } else if (e.key === "Escape" && isLightboxOpen) {
        setIsLightboxOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, venue.gallery.length]);

  const [similarVenues, setSimilarVenues] = useState<Venue[]>([]);

  useEffect(() => {
    VenueService.getVenues({ category: venue.category })
      .then(list => setSimilarVenues(list.filter(v => v.id !== venue.id).slice(0, 3)))
      .catch(() => setSimilarVenues([]));
  }, [venue.id, venue.category]);

  const hasCoordinates = venue.latitude != null && venue.longitude != null;

  const coordinates: [number, number] = hasCoordinates
    ? [Number(venue.latitude), Number(venue.longitude)]
    : (CITY_COORDINATES[venue.location] || CITY_COORDINATES["Ibadan"]);

  const mapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venue.name}, ${venue.location}, Oyo State`)}`;

  return (
    <>
      <article className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <button onClick={() => router.history.back()} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary">
          <ChevronLeft className="size-4" /> Back
        </button>
        <header className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              {venue.facility}
            </span>
          </div>
          <div className="space-y-0.5">
            <h1 className="font-display text-lg font-bold leading-none sm:text-xl">{venue.name}</h1>
            <div className="grid gap-1 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="space-y-0.5 text-muted-foreground">
                <p className="flex flex-wrap items-center gap-1.5 text-sm text-foreground/90">
                  <MapPin className="size-3.5" /> {venue.location}, Oyo State
                </p>
                <div className="flex flex-wrap gap-2">
                  {venue.capacity && (
                    <span className="rounded-full bg-accent/70 px-3 py-1 text-xs font-semibold text-foreground">
                      {venue.capacity} guests
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-primary/10 p-2 text-primary">
                  <Star className="size-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{hasGuestRating(venue) ? venue.rating.toFixed(1) : "New"}</p>
                </div>
              </div>
              {venue.bookingCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-accent/70 p-2 text-foreground">
                    <BookmarkCheck className="size-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{venue.bookingCount} booking{venue.bookingCount !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Premium Gallery Carousel & Lightbox */}
        <section className="mt-8 overflow-hidden">
          <div className="grid gap-2 lg:grid-cols-[1.7fr_1fr] lg:grid-rows-[1fr_1fr] lg:items-stretch max-h-[280px] sm:max-h-[340px] lg:max-h-[420px]">
            <div className="group/carousel relative aspect-[2/1] lg:aspect-auto overflow-hidden rounded-2xl lg:rounded-xl bg-accent/30 shadow-elegant lg:row-span-2">
              {/* Active Carousel Image */}
              <img
                src={venue.gallery[active]}
                alt={`${venue.name} - slide ${active + 1}`}
                className="h-full w-full max-w-full object-cover transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/25 pointer-events-none" />

              <button
                onClick={() => setActive(prev => (prev - 1 + venue.gallery.length) % venue.gallery.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center size-10 rounded-full border border-white/20 bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-all hover:scale-105 z-30"
                aria-label="Previous image"
              >
                <ChevronLeft className="size-4" />
              </button>

              <button
                onClick={() => setActive(prev => (prev + 1) % venue.gallery.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center size-10 rounded-full border border-white/20 bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-all hover:scale-105 z-30"
                aria-label="Next image"
              >
                <ChevronRight className="size-4" />
              </button>

              <div className="absolute top-3 right-3 flex items-center gap-2 z-30">
                <button
                  onClick={() => setIsPlaying(prev => !prev)}
                  className={`flex items-center justify-center size-8 rounded-lg border border-white/10 text-white backdrop-blur-sm transition-all hover:scale-105 ${isPlaying ? 'bg-primary border-primary hover:bg-primary/95' : 'bg-black/40 hover:bg-black/60'}`}
                  title={isPlaying ? "Pause Slideshow" : "Play Slideshow"}
                >
                  {isPlaying ? <Pause className="size-4" /> : <Play className="size-4" />}
                </button>
                <button
                  onClick={() => setIsLightboxOpen(true)}
                  className="flex items-center justify-center size-8 rounded-lg border border-white/10 bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-all hover:scale-105"
                  title="View Fullscreen"
                >
                  <Maximize className="size-4" />
                </button>
              </div>

              <div className="absolute bottom-3 right-3 z-30">
                <span className="rounded-lg bg-black/50 border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                  {active + 1} / {venue.gallery.length}
                </span>
              </div>
            </div>

            <div className="hidden lg:relative lg:block overflow-hidden rounded-lg border border-border/60 aspect-[4/3] bg-accent/20 shadow-sm hover:shadow-md transition-shadow">
              <button
                onClick={() => setActive(1)}
                className="absolute inset-0"
                aria-label="View gallery image 2"
                title="View gallery image 2"
              >
                <img src={venue.gallery[1]} alt="" className="h-full w-full object-cover" />
              </button>
            </div>
            <div className="hidden lg:relative lg:block overflow-hidden rounded-lg border border-border/60 aspect-[4/3] bg-accent/20 shadow-sm hover:shadow-md transition-shadow">
              <button
                onClick={() => setActive(2)}
                className="absolute inset-0"
                aria-label="View gallery image 3"
                title="View gallery image 3"
              >
                <img src={venue.gallery[2] ?? venue.gallery[1]} alt="" className="h-full w-full object-cover" />
              </button>
            </div>
          </div>


          {/* Fullscreen Lightbox Modal */}
          {isLightboxOpen && (
            <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[999] flex flex-col justify-between p-4 sm:p-6 transition-all duration-300">
              {/* Lightbox Header */}
              <header className="flex items-center justify-between text-white w-full">
                <div>
                  <span className="text-xs font-semibold tracking-wider text-primary uppercase">{venue.facility}</span>
                  <h3 className="text-lg font-bold font-display leading-tight">{venue.name}</h3>
                </div>
                <button
                  onClick={() => setIsLightboxOpen(false)}
                  className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white transition-all hover:scale-105 border border-white/10"
                  aria-label="Close Fullscreen"
                >
                  <X className="size-5" />
                </button>
              </header>

              {/* Lightbox Main Image Area */}
              <div className="relative flex-1 flex items-center justify-center my-4 group/lightbox max-w-5xl mx-auto w-full">
                <img
                  src={venue.gallery[active]}
                  alt=""
                  className="max-h-[70vh] sm:max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl animate-in fade-in zoom-in-95 duration-300"
                />

                {/* Lightbox Left Nav */}
                <button
                  onClick={() => setActive(prev => (prev - 1 + venue.gallery.length) % venue.gallery.length)}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 flex items-center justify-center size-12 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm transition-all hover:scale-105"
                  aria-label="Previous Image"
                >
                  <ChevronLeft className="size-6" />
                </button>

                {/* Lightbox Right Nav */}
                <button
                  onClick={() => setActive(prev => (prev + 1) % venue.gallery.length)}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 flex items-center justify-center size-12 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm transition-all hover:scale-105"
                  aria-label="Next Image"
                >
                  <ChevronRight className="size-6" />
                </button>
              </div>

              {/* Lightbox Footer & Thumbnail Strip */}
              <footer className="w-full max-w-4xl mx-auto flex flex-col items-center gap-4">
                <span className="text-sm font-semibold text-white/60">
                  {active + 1} / {venue.gallery.length}
                </span>
                <div className="flex gap-2 overflow-x-auto py-1 max-w-full scrollbar-hide">
                  {venue.gallery.map((g: string, i: number) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={`relative overflow-hidden rounded-lg border-2 transition-all shrink-0 w-16 sm:w-20 aspect-[16/10] ${active === i ? "border-primary scale-[0.98] opacity-100" : "border-transparent opacity-40 hover:opacity-85"}`}
                      aria-label={`View photo ${i + 1}`}
                      title={`View photo ${i + 1}`}
                    >
                      <img src={g} alt="" className="size-full object-cover" />
                    </button>
                  ))}
                </div>
              </footer>
            </div>
          )}
        </section>

        {/* Photo Sections by Category */}
        {venue.photoSections && Object.keys(venue.photoSections).some(k => (venue.photoSections![k] || []).length > 0) && (
          <section className="mt-8">
            <h2 className="font-display text-xl font-semibold mb-4">Photos by area</h2>
            <div className="space-y-4">
              {(FACILITY_PHOTO_LABELS[venue.facility] || FACILITY_PHOTO_LABELS["Hotel"]).map(label => {
                const photos: string[] = venue.photoSections?.[label.key] || [];
                if (photos.length === 0) return null;
                return (
                  <div key={label.key}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{label.emoji}</span>
                      <span className="font-medium text-sm">{label.label}</span>
                      <span className="text-xs text-muted-foreground">({photos.length} photo{photos.length !== 1 ? "s" : ""})</span>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                      {photos.map((url: string, i: number) => (
                        <button
                          key={i}
                          onClick={() => {
                            const allPhotos = [...venue.gallery, ...photos];
                            const idx = allPhotos.indexOf(url);
                            setActive(idx >= 0 ? idx : 0);
                            setIsLightboxOpen(true);
                          }}
                          className="relative shrink-0 w-40 h-[120px] rounded-xl overflow-hidden border border-border/40 hover:scale-[1.02] hover:shadow-md transition-all"
                        >
                          <img src={url} alt={`${label.label} ${i + 1}`} className="size-full max-w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
          <div>
            <h2 className="font-display text-2xl font-semibold">About this venue</h2>
            <p className="mt-3 text-foreground/80 leading-relaxed">{venue.description}</p>

            {/* Category-specific details */}
            {CATEGORY_DETAILS[venue.facility] && (
              <div className="mt-8 rounded-2xl border border-border/60 bg-accent/20 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="size-5 text-primary" />
                  <h3 className="font-display text-lg font-semibold">{CATEGORY_DETAILS[venue.facility].headline}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{CATEGORY_DETAILS[venue.facility].description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {CATEGORY_DETAILS[venue.facility].features.map((f) => (
                    <span key={f} className="flex items-center gap-1.5 rounded-full bg-background border border-border/60 px-3 py-1.5 text-xs font-medium">
                      <Check className="size-3 text-primary" /> {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Room Types Section */}
            {venue.roomTypes && venue.roomTypes.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-xl font-semibold">Room types</h3>
                <p className="mt-1 text-sm text-muted-foreground">Choose a room type to see pricing and book.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {venue.roomTypes.map((rt: RoomType) => {
                    const isSelected = selectedRoomId === rt.id;
                    const coverImage = rt.images && rt.images.length > 0 ? rt.images[0] : venue.image;
                    return (
                      <div
                        key={rt.id}
                        className={`relative flex flex-col overflow-hidden rounded-xl border text-left transition-all hover:shadow-soft ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-soft"
                            : "border-border/60 bg-card hover:border-primary/30"
                        }`}
                      >
                        {/* Room cover — navigates to full room page */}
                        <Link
                          to="/room-detail/$venueId/$roomId"
                          params={{ venueId: venue.id, roomId: rt.id }}
                          className="w-full"
                        >
                          {coverImage && (
                            <div className="relative aspect-[16/9] w-full overflow-hidden">
                              <img src={coverImage} alt={rt.name} className="size-full max-w-full object-cover transition-transform duration-500 hover:scale-105" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent" />
                              {rt.images.length > 1 && (
                                <span className="absolute bottom-2 right-2 rounded-md bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                                  +{rt.images.length - 1} more
                                </span>
                              )}
                              <span className="absolute top-2 left-2 rounded-md bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                                View details →
                              </span>
                            </div>
                          )}
                        </Link>
                        <div className="p-3 flex flex-col flex-1">
                          {isSelected && (
                            <div className="absolute top-3 right-3 grid size-6 place-items-center rounded-full bg-primary text-primary-foreground z-10">
                              <Check className="size-3.5" />
                            </div>
                          )}
                          <Link to="/room-detail/$venueId/$roomId" params={{ venueId: venue.id, roomId: rt.id }} className="text-left">
                            <div className="flex items-center gap-2">
                              <Crown className={`size-3.5 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                              <span className="font-display text-sm font-semibold">{rt.name}</span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{rt.description}</p>
                          </Link>
                          <div className="mt-3 flex items-end justify-between">
                            <div>
                              <span className="font-display text-base font-bold text-foreground">{formatNaira(rt.price)}</span>
                              <span className="text-[10px] text-muted-foreground"> / {venue.category === "hotel" ? "night" : "event"}</span>
                            </div>
                            <button
                              onClick={() => setSelectedRoomId(rt.id)}
                              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-accent text-foreground hover:bg-primary hover:text-primary-foreground"
                              }`}
                            >
                              {isSelected ? "Selected ✓" : "Select"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h3 className="mt-8 font-display text-xl font-semibold">Amenities</h3>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {venue.amenities.map((a: string) => (
                <li key={a} className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-3 text-sm">
                  <Check className="size-4 text-primary" /> {a}
                </li>
              ))}
            </ul>

            {/* Interactive Map Preview */}
            <div className="mt-8 aspect-video w-full overflow-hidden rounded-2xl border border-border/50 bg-muted relative group/map">
              {isMounted ? (
                <>
                  <Map
                    center={coordinates}
                    zoom={hasCoordinates ? zoom : Math.min(zoom, 10)}
                    onBoundsChanged={({ zoom }) => setZoom(zoom)}
                    provider={mapMode === "street" ? voyagerProvider : esriSatelliteProvider}
                    dprs={[1, 2]}
                    mouseEvents={true}
                    touchEvents={true}
                  >
                    {hasCoordinates && (
                      <Marker
                        anchor={coordinates}
                        onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
                      >
                        <div className="relative group cursor-pointer -translate-x-1/2 -translate-y-full block">
                          <div className="absolute -inset-2 rounded-full bg-primary/25 animate-ping opacity-75 pointer-events-none" />
                          <div className="relative flex items-center justify-center size-9 rounded-full bg-primary text-primary-foreground border-2 border-background shadow-lg transition-transform group-hover:scale-110">
                            <MapPin className="size-4 fill-primary-foreground text-primary-foreground" />
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none z-50">
                            <div className="bg-foreground text-background text-[11px] font-bold py-1 px-2.5 rounded-lg shadow-elegant border border-border/20 whitespace-nowrap flex items-center gap-1">
                              <span>{venue.name}</span>
                              <ExternalLink className="size-3 text-background/70" />
                            </div>
                            <div className="size-1.5 bg-foreground rotate-45 mx-auto -mt-1 border-r border-b border-border/20" />
                          </div>
                        </div>
                      </Marker>
                    )}
                  </Map>

                  <div className="absolute bottom-4 right-4 flex flex-col gap-1.5 z-40">
                    <button
                      onClick={() => setZoom(prev => Math.min(prev + 1, 18))}
                      className="p-2 rounded-lg bg-background/90 hover:bg-background text-foreground border border-border/50 shadow-soft hover:shadow transition-all"
                      aria-label="Zoom in"
                    >
                      <Plus className="size-3.5" />
                    </button>
                    <button
                      onClick={() => setZoom(prev => Math.max(prev - 1, 6))}
                      className="p-2 rounded-lg bg-background/90 hover:bg-background text-foreground border border-border/50 shadow-soft hover:shadow transition-all"
                      aria-label="Zoom out"
                    >
                      <Minus className="size-3.5" />
                    </button>
                  </div>

                  <div className="absolute top-4 left-4 z-40 pointer-events-none">
                    <div className="flex items-center gap-1.5 rounded-lg bg-background/90 border border-border/50 py-1.5 px-3 shadow-soft text-xs font-semibold text-foreground backdrop-blur-sm">
                      <MapIcon className="size-3.5 text-primary" /> {venue.location}, Oyo State
                    </div>
                  </div>

                  <div className="absolute bottom-4 left-4 z-40">
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 rounded-lg bg-background/90 hover:bg-background border border-border/50 py-1.5 px-3 shadow-soft hover:shadow text-xs font-semibold text-foreground backdrop-blur-sm transition-all"
                    >
                      <ExternalLink className="size-3.5 text-primary" />
                      Directions
                    </a>
                  </div>

                  <div className="absolute top-4 right-4 z-40">
                    <button
                      onClick={() => setMapMode(prev => prev === "street" ? "satellite" : "street")}
                      className="flex items-center gap-1.5 rounded-lg bg-background/90 hover:bg-background border border-border/50 py-1.5 px-3 shadow-soft hover:shadow text-xs font-semibold text-foreground backdrop-blur-sm transition-all"
                    >
                      <Globe className="size-3.5 text-primary" />
                      {mapMode === "street" ? "Satellite View" : "Street View"}
                    </button>
                  </div>

                  {!hasCoordinates && (
                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/70 p-6 text-center">
                      <div className="max-w-md rounded-3xl border border-border/60 bg-card/95 p-6 shadow-soft">
                        <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-muted text-primary">
                          <MapIcon className="size-6" />
                        </div>
                        <h4 className="text-sm font-bold text-foreground tracking-wide">Exact location not pinned</h4>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                          We are showing the city location for this property. Tap "Directions" to open Google Maps.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="grid size-full place-items-center text-sm text-muted-foreground animate-pulse">
                  Initializing interactive map...
                </div>
              )}
            </div>
          </div>

          {/* Booking card */}
          <aside className="md:sticky md:top-24 md:self-start lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-elegant">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-primary font-semibold">Reserve</p>
                  {venue.roomTypes && venue.roomTypes.length > 0 && (
                    <p className="mt-2 text-sm font-semibold text-foreground">
                      {selectedRoom ? selectedRoom.name : "Best available rate"}
                    </p>
                  )}
                </div>
                <div className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  {hasGuestRating(venue) ? venue.rating.toFixed(1) : "New"}
                </div>
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold">{formatNaira(displayPrice)}</span>
                <span className="text-sm text-muted-foreground">/ {venue.category === "hotel" ? "night" : "event"}</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Includes service & taxes preview.</p>

              <Link
                to="/booking/$venueId"
                params={{ venueId: venue.id }}
                search={selectedRoom ? { roomTypeId: selectedRoom.id } : {}}
              >
                <Button size="lg" className="mt-5 w-full">Reserve now</Button>
              </Link>

              <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                <p className="rounded-2xl bg-accent/70 px-3 py-2">No charge until booking confirmed.</p>
                <p className="rounded-2xl bg-accent/70 px-3 py-2">Free cancellation available on selected rooms.</p>
              </div>

              {venue.roomTypes && venue.roomTypes.length > 1 && (
                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Choose room type</p>
                  <div className="flex flex-wrap gap-2">
                    {venue.roomTypes.map((rt: RoomType) => (
                      <button
                        key={rt.id}
                        onClick={() => setSelectedRoomId(rt.id)}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
                          selectedRoomId === rt.id
                            ? "bg-primary text-primary-foreground"
                            : "bg-background text-foreground border border-border/70 hover:border-primary/60"
                        }`}
                      >
                        {rt.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>

        <Reviews venueId={venue.id} venueRating={hasGuestRating(venue) ? venue.rating : 0} />

        {similarVenues.length > 0 && (
          <section className="mt-20">
            <h2 className="font-display text-2xl font-semibold">You might also like</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 md:grid-cols-3">
              {similarVenues.map((v) => <VenueCard key={v.id} venue={v} />)}
            </div>
          </section>
        )}
      </article>
    </>
  );
}


